import type HubspotEnrichmentSettings from "app/pages/sales-intelligence/modules/crm-enrichment/impl/hubspot/models/HubspotEnrichmentSettings";
import type { GetRequestOptions, IFetchService } from "app/services/fetchService";

import type {
    CrmEnrichmentJob,
    CrmEnrichmentJobDTO,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobPrice,
    CrmEnrichmentJobPriceDto,
    CrmEnrichmentJobRenameDTO,
    CrmEnrichmentJobRunHistoryParams,
    CrmEnrichmentJobRunHistoryResponse,
    CrmEnrichmentJobToggleDTO,
    CrmProvider,
} from "../../types/types";

import type { ICrmEnrichmentAPIService } from "./types";

export function createCrmEnrichmentAPIService(
    fetchService: Readonly<IFetchService>,
    crmProvider: CrmProvider,
): ICrmEnrichmentAPIService {
    const baseUrl = `/sales-api/${crmProvider}-enrichment/settings`;
    const jobsUrl = `${baseUrl}/jobs`;

    return {
        // TODO update type
        getSettings(options?: Readonly<GetRequestOptions>): Promise<HubspotEnrichmentSettings> {
            return fetchService.get(baseUrl, undefined, options);
        },
        createJob(
            job: Readonly<CrmEnrichmentJobDTO>,
            options?: Readonly<GetRequestOptions>,
        ): Promise<CrmEnrichmentJob> {
            return fetchService.post(jobsUrl, job, options);
        },
        getJobs(options?: Readonly<GetRequestOptions>): Promise<CrmEnrichmentJob[]> {
            return fetchService.get(jobsUrl, undefined, options);
        },
        getJob(jobId: string, options?: Readonly<GetRequestOptions>): Promise<CrmEnrichmentJob> {
            return fetchService.get(`${jobsUrl}/${jobId}`, undefined, options);
        },
        updateJob(job: Readonly<CrmEnrichmentJob>, options?: Readonly<GetRequestOptions>): Promise<void> {
            return fetchService.put(`${jobsUrl}/${job.id}`, job, options);
        },
        toggleJob(
            { jobId, value }: Readonly<CrmEnrichmentJobToggleDTO>,
            options?: Readonly<GetRequestOptions>,
        ): Promise<CrmEnrichmentJob> {
            return fetchService.put(`${jobsUrl}/${jobId}/activation-toggle?value=${value}`, undefined, options);
        },
        renameJob(
            { jobId, name }: Readonly<CrmEnrichmentJobRenameDTO>,
            options?: Readonly<GetRequestOptions>,
        ): Promise<CrmEnrichmentJob> {
            return fetchService.put(`${jobsUrl}/${jobId}/rename?name=${name}`, undefined, options);
        },
        removeJob(jobId: string, options?: Readonly<GetRequestOptions>): Promise<CrmEnrichmentJob> {
            return fetchService.delete(`${jobsUrl}/${jobId}`, undefined, options);
        },
        getPrice(
            object: Readonly<CrmEnrichmentJobObject>,
            options?: Readonly<GetRequestOptions>,
        ): Promise<CrmEnrichmentJobPrice> {
            return fetchService.get(`${baseUrl}/current-price?enrichmentObject=${object}`, undefined, options);
        },
        getEstimatedPrice(
            fields: Readonly<CrmEnrichmentJobPriceDto>,
            options?: Readonly<GetRequestOptions>,
        ): Promise<CrmEnrichmentJobPrice> {
            return fetchService.post(`${baseUrl}/estimated-price`, { fields }, options);
        },
        getJobRunHistory(
            jobId: string,
            { page, pageSize }: Readonly<CrmEnrichmentJobRunHistoryParams>,
            options?: Readonly<GetRequestOptions>,
        ): Promise<CrmEnrichmentJobRunHistoryResponse> {
            return fetchService.get(
                `${jobsUrl}/${jobId}/run-history?page=${page}&pageSize=${pageSize}`,
                undefined,
                options,
            );
        },
        getJobRunResultFileUrl(
            jobId: string,
            runId: string,
            options?: Readonly<GetRequestOptions>,
        ): Promise<string> {
            return fetchService.get(`${jobsUrl}/${jobId}/runs/${runId}/result-file-url`, undefined, options);
        },
    };
}
